import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  MenuBook,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import Loading from "components/Loading";
import { useTranslation } from "react-i18next";

/**
 * PdfViewerDialog
 *
 * Shared inline PDF preview dialog. Renders a document inside a full-screen-ish
 * Dialog with a title bar (open-in-new-tab + close) and a loading spinner while
 * the embedded PDF streams in.
 *
 * Props:
 * - open: whether the dialog is open
 * - url: the document download URL (null renders an empty-state message)
 * - title: heading shown in the title bar
 * - onClose: called when the dialog requests close
 */
const PdfViewerDialog = ({ open, url, title, onClose }) => {
  const { t } = useTranslation("home");
  const [loading, setLoading] = useState(true);

  // Reset the spinner whenever a new document is opened.
  useEffect(() => {
    if (open && url) setLoading(true);
  }, [open, url]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: { width: "90vw", height: "90vh", maxWidth: "1200px", m: 1 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1,
          px: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <MenuBook color="primary" sx={{ fontSize: "1.2rem", flexShrink: 0 }} />
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
            {title || t("pdfViewer.document")}
          </Typography>
        </Box>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}
        >
          {url && (
            <Tooltip title={t("pdfViewer.openInNewTab")}>
              <IconButton
                size="small"
                onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {url ? (
          <>
            {loading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Loading />
              </Box>
            )}
            <iframe
              src={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
              title={title || t("pdfViewer.frameTitle")}
              onLoad={() => setLoading(false)}
              style={{ width: "100%", height: "100%", border: "none", flex: 1 }}
            />
          </>
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography color="text.secondary">
              {t("pdfViewer.noUrl")}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PdfViewerDialog;
